import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const useLanguageDetection = () => {
  const { i18n } = useTranslation();
  const [isLanguageDetected, setIsLanguageDetected] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState<string | null>(null);

  useEffect(() => {
    const detectAndSetLanguage = () => {
      // Get browser language
      const browserLang = navigator.language || navigator.languages?.[0] || 'en';
      console.log('Browser language:', browserLang);
      
      // Check if user has previously set a language preference
      const savedLanguage = localStorage.getItem('i18nextLng');
      console.log('Saved language:', savedLanguage);
      
      let targetLanguage = 'en'; // Default
      
      if (savedLanguage && ['en', 'fr'].includes(savedLanguage)) {
        // User has a saved preference, use it
        targetLanguage = savedLanguage;
        console.log('Using saved language preference:', targetLanguage);
      } else {
        // No saved preference, detect from browser
        if (browserLang.startsWith('fr')) {
          targetLanguage = 'fr';
          console.log('Browser language is French, setting to fr');
        } else {
          targetLanguage = 'en';
          console.log('Browser language is not French, defaulting to en');
        }
      }
      
      setDetectedLanguage(targetLanguage);
      
      // Only change language if it's different from current
      if (i18n.language !== targetLanguage) {
        console.log('Changing language from', i18n.language, 'to', targetLanguage);
        i18n.changeLanguage(targetLanguage);
      }
      
      setIsLanguageDetected(true);
    };

    // Run detection when i18n is ready
    if (i18n.isInitialized) {
      detectAndSetLanguage();
    } else {
      i18n.on('initialized', detectAndSetLanguage);
    }

    return () => {
      i18n.off('initialized', detectAndSetLanguage);
    };
  }, [i18n]);

  const changeLanguage = (languageCode: string) => {
    console.log('Manually changing language to:', languageCode);
    i18n.changeLanguage(languageCode);
    setDetectedLanguage(languageCode);
  };

  const getBrowserLanguage = () => {
    const browserLang = navigator.language || navigator.languages?.[0] || 'en';
    return browserLang.startsWith('fr') ? 'fr' : 'en';
  };

  return {
    isLanguageDetected,
    detectedLanguage,
    currentLanguage: i18n.language,
    changeLanguage,
    getBrowserLanguage,
    isReady: i18n.isInitialized && isLanguageDetected
  };
}; 